import React from "react";
import { useNavigate } from "react-router-dom";
import { Plug, ExternalLink, Clock3 } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { Card, Button, Badge } from "../components/primitives";
import { useGmailAuth } from "../../../modules/email-insights/hooks/useGmailAuth";
import { APP_CATALOG } from "../../../modules/apps/appCatalog";
import { AppIcon } from "../../../modules/apps/AppIcon";

// Card shell shared by every app tile.
function AppCardShell({ app, statusBadge, actions }) {
  const t = useDashTheme();
  return (
    <Card interactive style={{ display: "flex", flexDirection: "column", gap: t.s[3], minHeight: 184 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{
          width: 44, height: 44, borderRadius: t.r.lg, display: "flex", alignItems: "center", justifyContent: "center",
          background: `${app.color}16`, boxShadow: `inset 0 0 0 1px ${app.color}22`, flexShrink: 0,
        }}>
          <AppIcon app={app} size={22} />
        </div>
        {statusBadge}
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, color: t.c.text }}>{app.name}</div>
        <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: 1.45 }}>{app.description}</div>
      </div>

      {actions}
    </Card>
  );
}

// Gmail reuses the existing OAuth connect flow (useGmailAuth) and links into
// the already-built Email Insights page rather than duplicating a connect UI.
function GmailAppCard({ app }) {
  const navigate = useNavigate();
  const gmailAuth = useGmailAuth();
  const isConnected = gmailAuth.isConnected;

  return (
    <AppCardShell
      app={app}
      statusBadge={isConnected ? <Badge tone="positive">Connected</Badge> : <Badge tone="neutral">Not connected</Badge>}
      actions={
        <Button
          variant={isConnected ? "secondary" : "primary"}
          onClick={() => isConnected ? navigate("/dashboard/email-insights") : gmailAuth.connect()}
          disabled={gmailAuth.loading}
          rightIcon={isConnected ? <ExternalLink size={14} /> : undefined}
          style={{ alignSelf: "flex-start" }}
        >
          {isConnected ? "Open Email Insights" : "Connect Gmail"}
        </Button>
      }
    />
  );
}

// Every other app: no connector module built yet, so it's a placeholder that
// still shows the concept (which apps a team can plan to connect).
function ComingSoonAppCard({ app }) {
  return (
    <AppCardShell
      app={app}
      statusBadge={<Badge tone="neutral"><Clock3 size={11} /> Coming soon</Badge>}
      actions={
        <Button variant="secondary" disabled style={{ alignSelf: "flex-start" }} title={`${app.name} connector is coming soon`}>
          Connect
        </Button>
      }
    />
  );
}

export default function AppConnectorsV2() {
  const t = useDashTheme();
  return (
    <PageShell
      title="Connectors"
      subtitle="Connect the apps your team uses so managers can see who's responding on time."
      icon={<Plug size={16} />}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: t.s[3] }}>
        {APP_CATALOG.map((app) => app.id === "gmail"
          ? <GmailAppCard key={app.id} app={app} />
          : <ComingSoonAppCard key={app.id} app={app} />)}
      </div>
    </PageShell>
  );
}
